import { MarkerType, Position } from "reactflow";
import { keyBy } from "lodash";

import { FLO_KIND } from "./floConstants";

/**
 * FLO 차트 그래프 빌더(레이아웃).
 * 백엔드 응답({ items, resources, routes, boms, routings }, camelCase)을 받아
 * React Flow가 쓰는 노드/엣지(좌표·핸들 포함)로 변환한다.
 * 색/선/영역 배경 등 시각 스타일은 scss/components/FloChart.scss(className)에서 처리한다.
 *
 * [전개 방식] BOM을 "전개 트리"로 펼친다. 즉 같은 품목이라도 소비 경로마다 별도 인스턴스로
 * 그린다(BOM 상세 그리드와 동일). 동일 원본 품목/라우트/자원은 data.dupKey로 묶어 배지·하이라이트로 표시한다.
 */

// 엣지 공통 "구조" prop — 경로 종류·화살표·라벨 배경 크기(시각 색상은 scss).
const floEdgeBase = {
  type: "smoothstep",
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
  labelBgPadding: [4, 2],
};

const GAP_M = 170; // 주 흐름(라인) 축 간격 = 좌우(LR). 대체 라우트 ○분기/병합·결합박스가 한 칼럼에 뭉치지 않게 확폭: 110→170
const GAP_C = 60; // 교차(cross) 축 간격 = 상하(LR). 세로만 더 촘촘하게: 68→60

// 단계 영역 밴드 보정값
const OFF = 32; // 노드 좌상단 기준 좌표를 코어 중심(64/2)으로 보정
const PAD = 48; // 밴드 바깥 여백
const NODE = 64; // 노드 코어 크기

// ── 안전 가드: 복잡/비정상 모델(특히 공유 부품이 많은 DAG)의 전개 폭발 방어 ──
// 이 차트는 "소비 경로마다 별도 인스턴스"로 전개하므로 공유 부품이 많으면 인스턴스가 지수적으로
// 늘 수 있다. 상한에 도달하면 더 전개하지 않고 부분 그래프로 표시한다(브라우저 멈춤/거대 그래프 방지).
const MAX_INSTANCES = 4000; // 전개 인스턴스(품목 노드) 상한
const MAX_DEPTH = 60; // 전개 깊이 상한(비정상적으로 깊은/순환성 BOM 방어)
// 대체 자재 노드 상한. 대체 노드는 "인스턴스"가 아니라 MAX_INSTANCES 에 잡히지 않으므로
// (원자재마다 대체가 여러 개면 노드 수가 곱으로 늘어난다) 별도 예산을 둔다.
const MAX_ALTSUP_NODES = 1500;

// 대체 자재 노드의 주 흐름축 위치 — 원자재 컬럼에서 상류로 이 비율만큼(0=원자재, 1=라우트 컬럼).
// 좌우 양쪽과 겹치지 않는 구간이 좁아 값의 근거를 남긴다 (GAP_M=170, 노드 코어 NODE=64, 박스 여백 16):
//  · 하한 0.47 — 이보다 작으면 ◇ 결합 박스 왼쪽 경계(원자재 컬럼 -16px)를 침범한다.
//  · 상한 0.62 — 이보다 크면 구성품 자신의 라우트/자원 노드(상류 한 칸, 오른쪽 끝 -106px)와 겹친다.
//    (구성품이 반제품이고 라우트가 2개면 상단 라우트가 정확히 대체 레인 높이에 온다.)
// 0.55 = 양쪽에서 약 13px 씩 떨어진 중앙값.
const ALTSUP_COL_FRAC = 0.55;

// 정션(연결 의미) 노드 ID — 생산 단계(인스턴스 key)별로 유일해야 한다.
const combId = (k) => `comb-${k}`; // ◇ 결합(AND)
const altRouteInId = (k) => `alt-route-in-${k}`; // ○ 라우트 택1 분기(구성품→라우트)
const altRouteOutId = (k) => `alt-route-out-${k}`; // ○ 라우트 택1 병합(라우트→품목)
// 자원 정션 id는 라우트별로 유일해야 하므로(자원은 라우트에 종속) 각 렌더 지점에서 pk+routeId 로 직접 만든다:
//   alt-res-${pk}-${routeId}(자원 택1/점선) · sim-res-${pk}-${routeId}-${gi}(동시 자원/실선)

// 주 흐름 / 보조(수직) 핸들 위치
// 주 흐름 핸들: 완제품(다운스트림)이 LR=오른쪽 / TB=위에 오므로, TB는 입력=아래·출력=위로 흐른다.
const MAIN_T = (isLR) => (isLR ? Position.Left : Position.Bottom); // 주 흐름 입력(업스트림/구성품 쪽)
const MAIN_S = (isLR) => (isLR ? Position.Right : Position.Top); // 주 흐름 출력(완제품 쪽)
const PERP_T = (isLR) => (isLR ? Position.Top : Position.Left); // 자원 클러스터에서 받음
const PERP_S = (isLR) => (isLR ? Position.Bottom : Position.Right); // 주 흐름 쪽으로 보냄

const ROUTE_RES_IN = "resIn"; // 라우트가 자원 택일 정션을 받는 보조 핸들 id

// 대체 관계선(대체 자재 → 대체 대상 품목) 전용 핸들.
// 대체 노드는 대상보다 "위"에 놓이므로 위→아래로 내려오게 해야 자연스럽다(주 흐름 핸들을 쓰면
// 투입선과 같은 지점에서 출발해 겹치고, 대각선이 되어 직교 언어에서 튄다).
// 완제품 대체는 같은 컬럼이라 이 핸들 쌍으로 완전한 세로 직선이 된다.
const ALTSUP_LINK_OUT = "altsupOut"; // 대체 자재가 대상으로 내려보내는 source 핸들
const ALTSUP_LINK_IN = "altsupIn"; // 대체 대상 품목이 위에서 받는 target 핸들

/**
 * BOM 다단계(여러 생산 단계)를 전개 트리로 펼쳐 좌표/핸들을 배치한 그래프를 반환한다.
 *
 * 누적 컬럼 배치: 결합(구성품→◇)·분기(○→라우트)·합류(라우트→품목) 엣지마다 한 열을 확보하고,
 * ◇가 없는 단계는 컬럼을 당겨 빈칸이 생기지 않게 한다.
 *
 * 정션(일반 노드보다 훨씬 작게):
 * - 결합(◇, AND) : 구성품을 함께 소비.
 * - 택일(○, OR)  : 라우트 택1 / 자원 택1.
 *
 * @param {{items: Array, resources: Array, routes: Array, boms: Array, routings: Array}} data 백엔드 응답
 * @param {"TB"|"LR"} direction TB=위→아래, LR=좌→우
 * @param {boolean} hideResource true면 자원 노드·자원택1 정션·관련 엣지를 제외한다.
 * @param {boolean} resourceExpanded false면 단계별 대표 자원 1개(접힘), true면 전체(펼침).
 * @param {boolean} hideRatio true면 BOM 소비 비율(x{cmptQty}) 라벨을 숨긴다.
 * @param {boolean} hideAltSupply true면 대체 자재(z_alt_supply) 노드·엣지를 제외한다.
 * @returns {{nodes: Array, edges: Array}}
 */
export function buildFloGraph(
  data,
  direction = "LR",
  hideResource = false,
  resourceExpanded = false,
  hideRatio = false,
  hideAltSupply = false
) {
  const isLR = direction === "LR";

  // ── 응답 정규화 ───────────────────────────────────────────────────────
  // ID는 Number.MAX_SAFE_INTEGER(약 9e15)를 초과하는 값이 많고, restApi 가 json-bigint
  // (useNativeBigInt)로 큰 정수를 BigInt 로 파싱한다. Number() 변환 시 정밀도가 깨져
  // 노드/엣지 id 불일치·조회 실패가 발생하므로 모든 id를 문자열로 통일한다.
  const sid = (v) => (v == null ? v : String(v));
  const itemList = (data?.items ?? []).map((it) => ({ ...it, id: sid(it.id) }));
  const resList = (data?.resources ?? []).map((r) => ({ ...r, id: sid(r.id) }));
  const routes = (data?.routes ?? []).map((r) => ({ ...r, id: sid(r.id), itemId: sid(r.itemId) }));
  const boms = (data?.boms ?? []).map((b) => ({
    ...b,
    id: sid(b.id),
    itemId: sid(b.itemId),
    cmptId: sid(b.cmptId),
    cmptQty: b.cmptQty == null ? b.cmptQty : Number(b.cmptQty),
  }));
  const routings = (data?.routings ?? []).map((rt) => ({
    ...rt,
    id: sid(rt.id),
    routeId: sid(rt.routeId),
    resId: sid(rt.resId),
    priority: rt.priority == null ? null : Number(rt.priority),
  }));
  // 동시 자원(z_sim_resource): 라우트별 main/sub 자원 pair. 자원 택1(OR)과 달리 "함께 사용(AND)"이므로
  // 실선 결합 박스로 렌더한다(자원 택1은 점선 박스). 없으면 빈 배열 → 기존 렌더 동작 유지.
  const simResources = (data?.simResources ?? []).map((s) => ({
    routeId: sid(s.routeId),
    mainResId: sid(s.mainResId),
    subResId: sid(s.subResId),
  }));
  // 대체 자재(z_alt_supply): 원자재(itemId)를 대신 쓸 수 있는 품목(altItemId). 원자재와 대체재는
  // 우선순위(priority) 있는 택1(OR) 관계다(MP 엔진도 원자재를 priority 0 으로 같은 그룹에 넣는다).
  // consmpRouteId = 투입 공정. null 이면 공정 무관(그 자재를 소비하는 모든 곳).
  // 위치(loc_id)는 백엔드에서 이미 무시·중복 제거되어 넘어온다. 없으면 빈 배열 → 기존 렌더 동작 유지.
  const altSupplies = (data?.altSupplies ?? []).map((a) => ({
    itemId: sid(a.itemId),
    altItemId: sid(a.altItemId),
    consmpRouteId: sid(a.consmpRouteId),
    priority: a.priority == null ? null : Number(a.priority),
  }));
  // 대체 품목 중 BOM 구성품을 가진 것. 대체는 하위를 전개하지 않으므로(리프) 우클릭 "FLO 조회"
  // 노출 판정은 bomsByParent 로 알 수 없어 백엔드가 따로 알려준다.
  const altItemsWithBom = new Set((data?.altItemsWithBom ?? []).map(sid));

  const items = keyBy(itemList, "id");
  // 자원 우선순위: z_resource 엔 priority 컬럼이 없으므로 라우팅 우선순위(최소값)로 대표 자원을 정한다.
  const resPriorityOf = {};
  routings.forEach((rt) => {
    const p = rt.priority ?? Number.MAX_SAFE_INTEGER;
    if (resPriorityOf[rt.resId] == null || p < resPriorityOf[rt.resId]) resPriorityOf[rt.resId] = p;
  });
  const resources = keyBy(
    resList.map((r) => ({ ...r, priority: resPriorityOf[r.id] ?? Number.MAX_SAFE_INTEGER })),
    "id"
  );

  // 생산되는 품목(라우트/BOM의 상위 품목) 집합
  const producedIds = [...new Set([...routes.map((r) => r.itemId), ...boms.map((b) => b.itemId)])];
  const producedSet = new Set(producedIds);

  // 빠른 조회용 인덱스 — 재귀 전개/스테이지 계산에서 반복 filter/some(O(n²))을 O(1)로 줄여
  // 큰 모델에서도 느려지지 않게 한다.
  const bomsByParent = {}; // itemId(상위) -> 구성품 BOM 목록
  boms.forEach((b) => { (bomsByParent[b.itemId] ??= []).push(b); });
  const routesByItem = {}; // itemId -> 그 품목의 라우트 목록
  routes.forEach((r) => { (routesByItem[r.itemId] ??= []).push(r); });
  const routingsByRoute = {}; // routeId -> 라우팅(라우트↔자원) 목록
  routings.forEach((rt) => { (routingsByRoute[rt.routeId] ??= []).push(rt); });
  const simPairsByRoute = {}; // routeId -> [[mainResId, subResId], ...] 동시 자원 pair
  simResources.forEach((s) => {
    if (s.routeId == null || s.mainResId == null || s.subResId == null) return;
    (simPairsByRoute[s.routeId] ??= []).push([s.mainResId, s.subResId]);
  });
  const altSupplyByItem = {}; // itemId(원자재) -> 대체 자재 목록(우선순위 순)
  altSupplies.forEach((a) => { (altSupplyByItem[a.itemId] ??= []).push(a); });
  const routesById = keyBy(routes, "id"); // 대체 자재의 투입 공정 코드 표시용

  // 라우트의 동시 자원(z_sim_resource)을 main 자원 기준으로 셋으로 나눈다.
  // 셋 = main + 그 main의 sub들. 서로 다른 main = 별개 셋. 공유되는 sub는 각 셋에 모두 포함한다
  // (union-find로 병합하지 않음 → [SET1,SET2]·[SET2,SET3]처럼 SET2를 공유하는 겹치는 셋을 그대로 표현).
  // 반환: resId 배열의 배열. 길이 ≥ 2 = 동시(AND) 셋, 길이 1 = 단독 자원(택1 후보).
  const groupSimByMain = (ids, stageRoutes) => {
    const idSet = new Set(ids);
    const setByMain = new Map(); // mainResId -> [main, ...subs] (순서 유지, 중복 제거)
    const inPair = new Set(); // 어떤 pair(main/sub)에든 속한 자원
    stageRoutes.forEach((r) => {
      (simPairsByRoute[r.id] ?? []).forEach(([main, sub]) => {
        if (!idSet.has(main) || !idSet.has(sub)) return;
        if (!setByMain.has(main)) setByMain.set(main, [main]);
        const arr = setByMain.get(main);
        if (!arr.includes(sub)) arr.push(sub);
        inPair.add(main);
        inPair.add(sub);
      });
    });
    const groups = [...setByMain.values()]; // 동시 셋(길이 ≥ 2)
    ids.forEach((id) => { if (!inPair.has(id)) groups.push([id]); }); // 단독 자원(택1 후보)
    return groups;
  };

  // 논리 좌표 (m=주 흐름 축, c=교차 축) → 실제 좌표
  const toPos = (m, c) => (isLR ? { x: m * GAP_M, y: c * GAP_C } : { x: c * GAP_C, y: m * GAP_M });
  // 그룹 내 i번째 노드의 교차축 오프셋
  const spread = (i, n, gap = 1.8) => (i - (n - 1) / 2) * gap;

  const nodes = [];
  let cMin = 0;
  let cMax = 0;
  const add = (node, m, c, nodeData) => {
    node.position = toPos(m, c);
    node.data = { ...nodeData, direction };
    nodes.push(node);
    if (c < cMin) cMin = c;
    if (c > cMax) cMax = c;
  };

  // ── BOM 전개 트리(인스턴스) ───────────────────────────────────────────
  // 같은 품목이라도 소비 경로마다 별도 "인스턴스"로 전개한다(그리드와 동일한 트리).
  // → 공유 원자재/반제품이 단계마다 각각 그려져 교차선이 줄고, 한 구성품이 여러 결합 박스에
  //   동시에 속하던 문제(DAG)가 사라진다. 동일 원본은 dupKey(품목/라우트/자원 id)로 묶어 표시한다.
  const instances = {}; // key -> { key, itemId, cmptQty, parentKey, childKeys, depth }
  let instCounter = 0;
  let truncated = false; // 상한 도달로 일부를 전개하지 못했는지(부분 그래프 여부)
  // BOM 순환(재귀) 경로 — 품목코드 체인 문자열(예: "ItemB → ItemA → ItemB"). 중복 제거, 일정 개수만 보고.
  const cycleSet = new Set();
  const MAX_CYCLE_REPORT = 5;
  // depth = BOM 전개 깊이(루트=0). stage 계산의 기준이 되며, 같은 품목도 소비 경로마다 depth가 다를 수 있다.
  const buildInstance = (itemId, bom, parentKey, ancestors, depth) => {
    const key = `n${instCounter++}`;
    const cyclic = ancestors.has(itemId); // BOM 순환 방어(무한 전개 차단)
    if (cyclic && cycleSet.size < MAX_CYCLE_REPORT) {
      // ancestors(루트→부모 순서)에서 itemId가 처음 나온 지점부터가 순환 고리 → 다시 itemId 로 닫는다.
      const chain = [...ancestors];
      const loop = [...chain.slice(chain.indexOf(itemId)), itemId];
      cycleSet.add(loop.map((id) => items[id]?.itemCd ?? id).join(" → "));
    }
    const inst = { key, itemId, cmptQty: bom?.cmptQty ?? null, parentKey, childKeys: [], depth };
    instances[key] = inst;
    // 깊이/인스턴스 상한 도달 시 더 전개하지 않고 리프로 둔다(부분 그래프). 순환은 truncated 로 보지 않음.
    const overLimit = depth >= MAX_DEPTH || instCounter >= MAX_INSTANCES;
    if (!cyclic && overLimit) truncated = true;
    if (!cyclic && !overLimit) {
      const nextAnc = new Set(ancestors);
      nextAnc.add(itemId);
      inst.childKeys = (bomsByParent[itemId] ?? []).map((cb) => buildInstance(cb.cmptId, cb, key, nextAnc, depth + 1));
    }
    return key;
  };
  const consumed = new Set(boms.map((b) => b.cmptId));
  const rootIds = producedIds.filter((id) => !consumed.has(id)); // 어디서도 소비되지 않는 최종 생산 품목
  const rootSeed = rootIds.length ? rootIds : producedIds.length ? producedIds : itemList.map((it) => it.id);
  const rootKeys = rootSeed.map((rid) => buildInstance(rid, null, null, new Set(), 0));

  // ── 대체 자재 적용 판정 (인스턴스별) ─────────────────────────────────
  // 대체는 두 자리에 붙을 수 있다:
  //  (1) 소비되는 구성품 — 투입 공정(consmpRouteId)이 지정된 행은 그 라우트가 부모(= 이 구성품을
  //      소비하는 상위 품목)의 라우트일 때만 적용한다. 투입 공정은 항상 소비하는 쪽 상위 품목의
  //      라우트이므로, 무관한 라우트를 가리키는 행은 이 단계에 해당하지 않는다.
  //  (2) 완제품(루트) 자체 — 완제품은 어디에도 투입되지 않으므로 투입 공정이 NULL 인 행만 해당한다
  //      ("CHAIR 대신 CHAIR2 를 공급"). 라우트가 지정된 행은 소비 지점을 가리키므로 루트엔 안 붙인다.
  // 판정 자체는 토글과 무관하게 항상 한다 — 우클릭 "대체 공급" 메뉴는 대체를 숨긴 상태에서도
  // 노출돼야 하기 때문. 토글은 렌더/레이아웃 소비처(altSupplyCountOf)에서만 반영한다.
  const altSupplyOfInst = {}; // 인스턴스 key -> 적용되는 대체 행 목록
  Object.values(instances).forEach((inst) => {
    const rows = altSupplyByItem[inst.itemId];
    if (!rows?.length) return;
    let applied;
    if (inst.parentKey == null) {
      applied = rows.filter((a) => a.consmpRouteId == null); // 완제품: 공정 무관 행만
    } else {
      const parentRouteIds = new Set((routesByItem[instances[inst.parentKey].itemId] ?? []).map((r) => r.id));
      applied = rows.filter((a) => a.consmpRouteId == null || parentRouteIds.has(a.consmpRouteId));
    }
    if (applied.length) altSupplyOfInst[inst.key] = applied;
  });
  const hasAltSupplyOf = (key) => (altSupplyOfInst[key]?.length ?? 0) > 0; // 메뉴 노출용(토글 무관)
  // 레이아웃/렌더용 개수. 숨기면 0 → 행 예약·maxStage 보정·노드 생성이 모두 꺼진다
  // (hideResource 가 RES_PAD 를 0 으로 만드는 것과 같은 방식).
  const altSupplyCountOf = (key) => (hideAltSupply ? 0 : altSupplyOfInst[key]?.length ?? 0);
  let altSupplyNodeCount = 0; // MAX_ALTSUP_NODES 예산 소비량

  // 생산 인스턴스 = 구성품(자식)이 있거나 라우트가 있는 인스턴스(단계를 가짐)
  const isProducedInst = (key) => {
    const inst = instances[key];
    return inst.childKeys.length > 0 || (routesByItem[inst.itemId]?.length ?? 0) > 0;
  };

  // ── 단계(stage) 기반 컬럼 배치 ─────────────────────────────────────────
  // 인스턴스 BOM depth로 stage 부여: 품목 = 2·depth, 그 품목을 만드는 라우트 = 2·depth+1.
  // 검색 품목(루트, depth 0) = stage 0. 같은 품목이라도 소비 경로(depth)가 다르면 stage가 다르다.
  // 주 흐름 컬럼 m = maxStage - stage → 완제품(stage 0)을 끝(LR=오른쪽 / TB=아래)에 둔다.
  // 결합(◇)·라우트택1(○) 정션은 별도 컬럼/밴드를 차지하지 않고 라우트 stage 안에 들어간다.
  let maxStage = 0;
  Object.values(instances).forEach((inst) => {
    // 구성품의 대체 노드는 품목 컬럼보다 상류(ALTSUP_COL_FRAC)에 걸쳐 놓이므로 리프여도 stage 를
    // 한 칸 더 필요로 한다. 반영하지 않으면 최대 깊이의 대체 노드 일부가 단계 밴드 밖(맨 배경)에 그려진다.
    // (컬럼과 밴드가 같은 mOf/maxStage 를 쓰므로 한 칸 늘어나도 stage별 밴드 색은 그대로다.)
    // 완제품(루트)의 대체는 같은 컬럼에 두므로 여기서 제외한다 — 넣으면 빈 밴드가 하나 늘어난다.
    const hasSideAltSupply = inst.parentKey != null && altSupplyCountOf(inst.key) > 0;
    const occupiesRouteCol = isProducedInst(inst.key) || hasSideAltSupply;
    const top = occupiesRouteCol ? 2 * inst.depth + 1 : 2 * inst.depth;
    if (top > maxStage) maxStage = top;
  });
  // stage → 주 흐름 컬럼. LR: 완제품(stage 0)을 오른쪽 끝, TB: 완제품을 위쪽 끝에 둔다.
  const mOf = (stage) => (isLR ? maxStage - stage : stage);
  const instCol = {};
  const stageOf = {}; // 생산 인스턴스 key -> 단계 정보
  Object.values(instances).forEach((inst) => {
    const productCol = mOf(2 * inst.depth);
    instCol[inst.key] = productCol;
    if (!isProducedInst(inst.key)) return; // 리프(원자재)는 품목 컬럼만 차지
    const itemRoutes = routesByItem[inst.itemId] ?? [];
    stageOf[inst.key] = {
      key: inst.key,
      p: inst.itemId,
      hasComb: inst.childKeys.length > 1, // 구성품 2개 이상 → ◇ 결합(AND)
      hasAltRoute: itemRoutes.length > 1, // 라우트 2개 이상 → ○ 택1(OR)
      routeCol: mOf(2 * inst.depth + 1), // 라우트 = stage 2·depth+1
      productCol,
      routes: itemRoutes,
      childKeys: inst.childKeys,
    };
  });

  // 결합(◇) 박스에 속한 구성품 인스턴스 key → 박스 id. 구성품 노드를 드래그할 때
  // 같은 박스의 형제 구성품·박스를 함께 옮기기 위해 각 구성품 노드 data.combGroup에 심는다.
  const combGroupOfChild = {};
  Object.values(stageOf).forEach((s) => {
    if (s.hasComb) s.childKeys.forEach((ck) => { combGroupOfChild[ck] = combId(s.key); });
  });

  // ── 교차축(행) 배치: 순수 트리이므로 타이디-레이아웃이 정확 ─────────────
  // 리프(원자재)는 순차 행을 차지하고, 상위 품목은 첫·마지막 자식의 중앙에 놓여 중앙 정렬된다.
  //
  // [자원 레인] 자원 분기는 라우트에서 "위쪽(수직)"으로 RES_PAD 행만큼 뻗는다. 평행 단계가
  // 세로로 쌓일 때 아래 단계의 자원이 위 단계 영역을 침범하므로, 자원을 가진 서브트리 앞(위)에
  // RES_PAD 행을 비워 레인을 확보한다. (확장/축소 토글 시 트렁크가 흔들리지 않도록 항상 예약)
  const ROW_GAP = 1.6; // 행 간격(c 단위)
  // 자원이 위로 뻗는 폭만큼 비울 행 수. 펼치면 자원을 세로로 쌓으므로 레인을 더 확보(겹침 방지).
  const RES_PAD = hideResource ? 0 : resourceExpanded ? 3.5 : 1.7;

  const hasOwnRes = (itemId) => (routesByItem[itemId] ?? []).some((r) => (routingsByRoute[r.id]?.length ?? 0) > 0);
  const subtreeResMemo = {};
  const subtreeHasRes = (key) => {
    if (subtreeResMemo[key] != null) return subtreeResMemo[key];
    const inst = instances[key];
    const res = hasOwnRes(inst.itemId) || inst.childKeys.some(subtreeHasRes);
    subtreeResMemo[key] = res;
    return res;
  };

  // [대체 자재 레인] 대체 노드도 자원처럼 원자재에서 "위쪽"으로 뻗는다. 서브트리 안에서 한 인스턴스가
  // 갖는 최대 대체 개수만큼 앞(위)에 행을 비워 이전 형제 서브트리와 겹치지 않게 한다(RES_PAD와 동일 성격).
  const ALTSUP_GAP = 1.3; // 대체 노드 세로 간격(행 단위)
  const subtreeAltSupplyMemo = {};
  const subtreeAltSupplyPad = (key) => {
    if (subtreeAltSupplyMemo[key] != null) return subtreeAltSupplyMemo[key];
    const inst = instances[key];
    let pad = altSupplyCountOf(key);
    inst.childKeys.forEach((ck) => {
      const kid = subtreeAltSupplyPad(ck);
      if (kid > pad) pad = kid;
    });
    subtreeAltSupplyMemo[key] = pad;
    return pad;
  };

  const rowOf = {};
  // 인스턴스 key -> 그 원자재의 대체 노드들이 놓일 교차축 좌표(c 단위, cOf 와 같은 단위).
  // 행 번호가 아니라 c 단위로 저장한다 — add() 는 c 를 받고, 행→c 변환은 ROW_GAP 배다.
  const altSupplyCsOf = {};
  let nextLeaf = 0;
  const placeSiblings = (keys) =>
    keys.map((key, i) => {
      // i > 0 만 패딩하는 이유: 첫 형제의 위쪽은 레이아웃 최상단이라 이미 비어 있다(음수 행은
      // add()가 cMin으로 추적해 단계 밴드가 함께 확장된다).
      if (i > 0 && RES_PAD && subtreeHasRes(key)) nextLeaf += RES_PAD;
      if (i > 0) nextLeaf += subtreeAltSupplyPad(key) * ALTSUP_GAP;
      return assignRow(key);
    });
  const assignRow = (key) => {
    const inst = instances[key];
    let r;
    if (inst.childKeys.length === 0) {
      r = nextLeaf;
      nextLeaf += 1;
    } else {
      const kidRows = placeSiblings(inst.childKeys);
      r = (kidRows[0] + kidRows[kidRows.length - 1]) / 2; // 첫·마지막 자식의 중앙
    }
    rowOf[key] = r;
    const altN = altSupplyCountOf(key);
    // 원자재 행에서 위쪽으로 ALTSUP_GAP 행씩 쌓는다. 저장은 c 단위(= 행 × ROW_GAP).
    if (altN) altSupplyCsOf[key] = Array.from({ length: altN }, (_, i) => (r - (i + 1) * ALTSUP_GAP) * ROW_GAP);
    return r;
  };
  placeSiblings(rootKeys);

  const cOf = (key) => (rowOf[key] ?? 0) * ROW_GAP;

  // ── 품목 노드 (인스턴스별) ──
  Object.values(instances).forEach((inst) => {
    add({ id: `item-${inst.key}`, type: "itemNode" }, instCol[inst.key], cOf(inst.key), {
      label: items[inst.itemId]?.itemCd,
      kind: FLO_KIND.ITEM,
      role: producedSet.has(inst.itemId) ? "product" : "component",
      ratio: hideRatio ? null : inst.cmptQty ?? null, // BOM 소비 비율(x{qty}) — 노드 코너 배지로 표시
      raw: items[inst.itemId],
      hasChildren: (bomsByParent[inst.itemId]?.length ?? 0) > 0, // BOM 구성품 보유 여부(우클릭 FLO 조회 활성화 기준)
      isRoot: inst.depth === 0, // 검색 루트(stage 0) 품목 여부 — 우클릭 이동 액션의 stage 0 조건에 사용
      hasAltSupply: hasAltSupplyOf(inst.key), // 대체 자재 보유 여부(우클릭 "대체 공급" 노출 기준, 토글 무관)

      dupKey: `item:${inst.itemId}`, // 동일 원본 품목 묶음(배지/하이라이트)
      combGroup: combGroupOfChild[inst.key], // 결합 박스 소속 시 박스 id(comb-…) — 함께 이동용
      targetPos: MAIN_T(isLR),
      sourcePos: MAIN_S(isLR),
      // 대체 자재가 있을 때만 위에서 받는 보조 핸들을 만든다(없는 노드에 붙이면 빈 핸들만 늘어난다)
      extraHandles: altSupplyCountOf(inst.key)
        ? [{ id: ALTSUP_LINK_IN, type: "target", position: PERP_T(isLR) }]
        : undefined,
    });
  });

  const edges = [];
  const groupNodes = []; // 그룹 박스(결합/택1) — 배경 밴드 위, 일반 노드 아래 레이어

  // 노드 위치 집합을 감싸는 그룹 박스 노드 생성 (결합=실선 / 택1=점선)
  const makeGroupBox = (id, pts, extra) => {
    const minX = Math.min(...pts.map((pt) => pt.x));
    const maxX = Math.max(...pts.map((pt) => pt.x));
    const minY = Math.min(...pts.map((pt) => pt.y));
    const maxY = Math.max(...pts.map((pt) => pt.y));
    const BOXPAD = 16;
    return {
      id,
      type: "combineGroupNode",
      position: { x: minX - BOXPAD, y: minY - BOXPAD },
      data: { width: maxX - minX + NODE + 2 * BOXPAD, height: maxY - minY + NODE + 2 * BOXPAD, direction, ...extra },
      draggable: false,
      selectable: false,
      connectable: false,
      focusable: false,
      zIndex: -1,
      style: { zIndex: -1 },
    };
  };

  /**
   * 인스턴스 key 의 대체 자재 노드 + 엣지를 만든다. 구성품(단계 안)과 완제품(루트) 양쪽이 공유한다.
   * @param {string} key 대체가 붙는 인스턴스(원자재 또는 완제품)
   * @param {number} col 대체 노드를 놓을 주 흐름 컬럼
   * @param {(row: object) => string|null} inputTargetOf 투입선을 걸 노드 id. null 이면 투입선 생략
   *        (완제품은 어디에도 투입되지 않으므로 대체 관계선만 그린다).
   */
  const addAltSupplyNodes = (key, col, inputTargetOf) => {
    if (altSupplyCountOf(key) === 0) return; // 토글로 숨김이거나 적용되는 대체가 없음
    const altCs = altSupplyCsOf[key] ?? [];
    const sourceItemCd = items[instances[key].itemId]?.itemCd;

    altSupplyOfInst[key].forEach((a, ai) => {
      // 노드 예산 초과 시 더 그리지 않고 부분 그래프로 표시(대체는 인스턴스가 아니라
      // MAX_INSTANCES 에 안 잡히므로 별도 상한이 필요하다).
      if (altSupplyNodeCount >= MAX_ALTSUP_NODES) {
        truncated = true;
        return;
      }
      altSupplyNodeCount += 1;
      // consmpRouteId 까지 id 에 넣는다 — 같은 (대상, 대체품목) 조합이 공정별로 여러 행일 수 있다.
      const nodeId = `altsup-${key}-${a.altItemId}-${a.consmpRouteId ?? "all"}`;
      add({ id: nodeId, type: "itemNode" }, col, altCs[ai] ?? cOf(key), {
        label: items[a.altItemId]?.itemCd,
        kind: FLO_KIND.ITEM,
        role: instances[key].parentKey == null ? "product" : "component",
        isAltSupply: true, // 노드 모양(해치 채움) 분기 + 사이드바/우클릭 분기
        altSupplyOf: sourceItemCd, // 대체 대상 품목 코드
        altSupplyRouteCd: a.consmpRouteId ? routesById[a.consmpRouteId]?.routeCd : null, // null = 공정 무관
        altSupplyPriority: a.priority, // 표시는 노드 클릭 시 사이드바에서만(라벨을 어지럽히지 않는다)
        ratio: hideRatio ? null : instances[key].cmptQty ?? null, // 소비량 컬럼이 없어 대상의 BOM 값을 따른다
        raw: items[a.altItemId],
        hasChildren: altItemsWithBom.has(a.altItemId), // 대체는 전개하지 않으므로 백엔드 판정을 사용
        isRoot: false, // 대체 자재 자신은 검색 루트가 아니다
        dupKey: `item:${a.altItemId}`, // 같은 품목이 다른 곳에 전개돼 있으면 함께 강조
        followsNode: `item-${key}`, // 대체 대상을 드래그하면 함께 이동
        targetPos: MAIN_T(isLR),
        sourcePos: MAIN_S(isLR),
        // 관계선 전용 출력 핸들 — 대상이 아래에 있으므로 아래로 내보낸다(투입선은 주 흐름 핸들 사용).
        extraHandles: [{ id: ALTSUP_LINK_OUT, type: "source", position: PERP_S(isLR) }],
      });

      const inputTarget = inputTargetOf(a);
      if (inputTarget) {
        edges.push({ ...floEdgeBase, id: `e-altsup-${nodeId}`, source: nodeId, target: inputTarget, className: "flo-edge-altsup" });
      }
      // 대체 관계선: 어느 품목의 대체인지 명시(구성품이 여러 개면 위치만으로는 모호하다).
      // 전용 핸들(위→아래)로 직교 경로를 만든다. 완제품 대체는 같은 컬럼이라 완전한 세로 직선이 된다.
      // 화살표는 없다(자재 흐름이 아니라 "대신 쓸 수 있다"는 관계).
      edges.push({
        ...floEdgeBase,
        id: `e-altsup-link-${nodeId}`,
        source: nodeId,
        sourceHandle: ALTSUP_LINK_OUT,
        target: `item-${key}`,
        targetHandle: ALTSUP_LINK_IN,
        className: "flo-edge-altsup-link",
        markerEnd: undefined,
      });
    });
  };

  // ── 단계별 결합/라우트택1/라우트/자원 + 엣지 ────────────────────────────
  Object.values(stageOf).forEach((s) => {
    const { key: pk, hasComb, hasAltRoute, routeCol } = s;
    const childKeys = s.childKeys;
    const stageRoutes = s.routes;
    const stageC = cOf(pk); // 단계 중심선 = 상위 품목 행(타이디 레이아웃: 자식들의 중앙)

    // 대체 라우트는 세로로 나란히 두고, 그 사이에 ○(택1 정션)이 들어갈 정도만 벌린다.
    // 자원은 각 라우트의 "바깥쪽"(위 라우트→위, 아래 라우트→아래)에 쌓으므로 라우트 간격에 자원 높이는 반영하지 않는다.
    const routeGap = stageRoutes.length <= 1 ? 1.6 : 2.6;

    // 결합(AND): 구성품 2개 이상일 때 구성품들을 감싸는 사각형 박스. 박스 단일 출력선이 다음으로 연결.
    if (hasComb) {
      const pts = childKeys.map((ck) => toPos(instCol[ck], cOf(ck)));
      groupNodes.push(makeGroupBox(combId(pk), pts, { label: "AND", targetPos: MAIN_T(isLR), sourcePos: MAIN_S(isLR) }));
      // 비율(x{qty})은 각 구성품 노드의 코너 배지로 표시(itemNode data.ratio) → 박스 경계와 겹치지 않음
    }

    // 라우트 (주 흐름 입력 + 자원 보조 입력 resIn) — 단계 중심선 기준 분산. 인스턴스별 복제.
    const routeCof = (i) => stageC + spread(i, stageRoutes.length, routeGap); // 이 라우트의 교차축 위치
    // 자원을 쌓는 방향: 대체 라우트면 중심선 위 라우트는 위(-1)로, 아래 라우트는 아래(+1)로 "바깥쪽"에 둔다.
    // (라우트 사이 중앙을 비워 ○/엣지와 겹치지 않게 한다.) 단일 라우트는 위(-1).
    const resDir = (i) => (hasAltRoute && routeCof(i) >= stageC ? 1 : -1);
    // 자원(보조) 핸들은 자원이 오는 쪽에 둔다: 위면 PERP_T, 아래면 PERP_S.
    const resInPos = (i) => (resDir(i) === -1 ? PERP_T(isLR) : PERP_S(isLR));
    stageRoutes.forEach((r, i) =>
      add({ id: `route-${pk}-${r.id}`, type: "routeNode" }, routeCol, routeCof(i), {
        label: r.routeCd,
        kind: FLO_KIND.ROUTE,
        raw: r,
        dupKey: `route:${r.id}`,
        targetPos: MAIN_T(isLR),
        sourcePos: MAIN_S(isLR),
        extraHandles: [{ id: ROUTE_RES_IN, type: "target", position: resInPos(i) }],
      })
    );

    // 라우트 택1(OR): 라우트를 박스로 감싸지 않고 앞뒤로 작은 ○(OR) 정션 2개로 브래킷한다.
    // 구성품 → ○(분기) → 각 라우트 → ○(병합) → 품목. 구성품/품목 쪽은 단선이라 깔끔하고,
    // 분기·병합은 라우트 바로 옆에서 짧게 일어나 item↔route 선이 엉키지 않는다. ○ 는 단계 중심선에 둔다.
    const dm = s.productCol - routeCol; // 다운스트림(품목) 방향 (LR:+1, TB:-1)
    if (hasAltRoute) {
      add({ id: altRouteInId(pk), type: "alternateNode", draggable: false }, routeCol - dm * 0.5, stageC, {
        label: "OR",
        targetPos: MAIN_T(isLR),
        sourcePos: MAIN_S(isLR),
      });
      add({ id: altRouteOutId(pk), type: "alternateNode", draggable: false }, routeCol + dm * 0.5, stageC, {
        label: "OR",
        targetPos: MAIN_T(isLR),
        sourcePos: MAIN_S(isLR),
      });
    }

    // 라우트가 없으면(BOM만 있는 단계) 상위 품목으로 직접 합류
    const singleRouteTarget = stageRoutes.length ? `route-${pk}-${stageRoutes[0].id}` : `item-${pk}`;
    // 업스트림(구성품/결합) 연결 대상: 대체 라우트면 분기 ○ 하나로 모으고(단선), 아니면 단일 라우트/품목.
    const upstreamTarget = hasAltRoute ? altRouteInId(pk) : singleRouteTarget;

    if (hasComb) {
      // 결합 박스 → 업스트림 대상(분기 ○ 또는 라우트) — 단일 출력선
      edges.push({ ...floEdgeBase, id: `e-comb-${pk}`, source: combId(pk), target: upstreamTarget });
    } else {
      // 결합 없음: 각 구성품 → 업스트림 대상. BOM cmptQty 는 구성품 노드 배지로 표시.
      childKeys.forEach((ck) => {
        edges.push({ ...floEdgeBase, id: `e-bom-${ck}`, source: `item-${ck}`, target: upstreamTarget });
      });
    }

    // 다운스트림: 대체 라우트면 분기 ○ → 각 라우트 → 병합 ○ → 품목. 아니면 라우트 → 품목 직접.
    const productTarget = `item-${pk}`;
    if (hasAltRoute) {
      // 분기/병합 팬은 직선으로 그려 라우트를 감싸는 '괄호' 모양(직교선)을 피한다 → 깔끔한 갈래 모양.
      stageRoutes.forEach((r) => {
        edges.push({ ...floEdgeBase, id: `e-altin-${pk}-${r.id}`, source: altRouteInId(pk), target: `route-${pk}-${r.id}`, type: "straight" });
        edges.push({ ...floEdgeBase, id: `e-rp-${pk}-${r.id}`, source: `route-${pk}-${r.id}`, target: altRouteOutId(pk), type: "straight" });
      });
      edges.push({ ...floEdgeBase, id: `e-altroute-out-${pk}`, source: altRouteOutId(pk), target: productTarget });
    } else {
      stageRoutes.forEach((r) => {
        edges.push({ ...floEdgeBase, id: `e-rp-${pk}-${r.id}`, source: `route-${pk}-${r.id}`, target: productTarget });
      });
    }

    // ── 대체 자재 (구성품별, 인스턴스별 복제) ──
    // 원자재를 대신 쓸 수 있는 품목을 원자재 바로 앞(상류)에 그리고 투입 공정으로 연결한다.
    // 컬럼: 원자재 컬럼과 그 구성품의 라우트 컬럼 "사이"(ALTSUP_COL_FRAC). 한 칸을 다 옮기면
    // 구성품이 반제품일 때 자기 라우트·자원 노드와 정면으로 겹친다(라우트 2개면 상단 라우트가
    // 정확히 대체 레인에 온다). ALTSUP_COL_FRAC 주석의 여백 계산 참고.
    // ※ 이 블록은 아래 `if (hideResource) return;` 보다 반드시 위에 있어야 한다 —
    //   자원 숨기기 토글이 대체 자재까지 숨기면 안 되기 때문(가드가 콜백을 early-return 시킨다).
    childKeys.forEach((ck) => {
      addAltSupplyNodes(ck, instCol[ck] - dm * ALTSUP_COL_FRAC, (a) =>
        // 투입선 대상: 공정 지정 → 그 라우트로 직결 / 공정 무관 → 원자재와 같은 대상(○ 또는 라우트/품목)
        a.consmpRouteId ? `route-${pk}-${a.consmpRouteId}` : upstreamTarget
      );
    });

    // ── 자원 분기 (라우트별, 인스턴스별 복제) ──
    // 자원은 각 라우트에 종속된다: "자원 택1(대체 자원)"·"동시 자원"은 모두 같은 라우트 안에서만 성립한다.
    // 대체 라우트가 여러 개면 라우트마다 자기 자원만 그 라우트 위쪽에 그려, 서로 다른 라우트가 자원을
    // 공유하는 것처럼 보이지 않게 한다(라우트별 자원 클러스터 + 그 라우트로만 연결).
    if (hideResource) return;
    const routeM = routeCol;
    const VRES = 1.5; // 자원 세로 간격(c 단위 — 노드+라벨 높이 확보)

    stageRoutes.forEach((r, ri) => {
      const routeC = routeCof(ri);
      const d = resDir(ri); // -1=라우트 위쪽, +1=라우트 아래쪽(대체 라우트의 바깥 방향)
      const resSrc = d === -1 ? PERP_S(isLR) : PERP_T(isLR); // 자원/박스가 라우트를 향하는 출력 핸들
      const routeNode = `route-${pk}-${r.id}`;
      const resIds = [...new Set((routingsByRoute[r.id] ?? []).map((rt) => rt.resId))];
      if (resIds.length === 0) return;

      const resNodeId = (id) => `res-${pk}-${r.id}-${id}`;
      const altBoxId = `alt-res-${pk}-${r.id}`;
      const simBoxId = (gi) => `sim-res-${pk}-${r.id}-${gi}`;

      if (!resourceExpanded) {
        // 접힘: 라우트별 대표 자원 1개(+N)만 표시(클릭 시 펼침). 그 라우트에 직접 연결.
        const repId = [...resIds].sort((a, b) => (resources[a]?.priority ?? 0) - (resources[b]?.priority ?? 0))[0];
        const more = resIds.length - 1;
        add({ id: `res-group-${pk}-${r.id}`, type: "resourceNode" }, routeM, routeC + d * 1.6, {
          label: more > 0 ? `${resources[repId]?.resCd} +${more}` : resources[repId]?.resCd,
          kind: FLO_KIND.RESOURCE,
          raw: resources[repId],
          dupKey: `res:${repId}`,
          collapsed: true,
          sourcePos: resSrc,
        });
        edges.push({ ...floEdgeBase, id: `e-resgroup-${pk}-${r.id}`, source: `res-group-${pk}-${r.id}`, target: routeNode, targetHandle: ROUTE_RES_IN });
        return;
      }

      if (resIds.length === 1) {
        // 자원 1개: 정션 없이 직접 연결(클릭 시 접기)
        const id = resIds[0];
        add({ id: resNodeId(id), type: "resourceNode" }, routeM, routeC + d * 1.8, {
          label: resources[id]?.resCd,
          kind: FLO_KIND.RESOURCE,
          raw: resources[id],
          dupKey: `res:${id}`,
          collapsible: true,
          sourcePos: resSrc,
        });
        edges.push({ ...floEdgeBase, id: `e-res1-${pk}-${r.id}`, source: resNodeId(id), target: routeNode, targetHandle: ROUTE_RES_IN });
        return;
      }

      // 자원 2개 이상: main 자원 기준 동시 셋(z_sim_resource)으로 그룹화. 길이≥2 = 동시(AND, 실선 박스),
      // 길이 1 = 단독 자원(택1 후보). 공유 sub 자원은 각 셋에 중복 노드로 표시하므로 (그룹,자원)마다
      // 고유 노드 id를 부여한다. 그룹 순서대로 평탄화해 바깥 방향으로 쌓는다.
      const resGroups = groupSimByMain(resIds, [r]);
      const simCount = resGroups.filter((g) => g.length > 1).length;
      const singleCount = resGroups.length - simCount;

      const instNodeId = (gi, id) => `res-${pk}-${r.id}-g${gi}-${id}`; // 그룹별 인스턴스(공유 자원 중복 렌더)
      let seq = 0;
      const groupInsts = resGroups.map((g, gi) =>
        g.map((id) => {
          const inst = { gi, id, nodeId: instNodeId(gi, id), c: routeC + d * (2.4 + seq * VRES) };
          seq += 1;
          return inst;
        })
      );
      const allInsts = groupInsts.flat();

      // 각 그룹의 소속 박스 id — 자원의 combGroup(드래그 시 함께 이동할 대상)이 된다.
      // 동시 셋은 "자기 실선 박스"로 묶여 셋별로 독립 이동한다(공유 자원도 셋마다 별개 인스턴스라 각자 이동).
      // 단독 자원은 바깥 점선 택1 박스로 묶는다.
      const onlyOneSim = simCount === 1 && singleCount === 0;
      let simSeq = 0;
      const boxIdOfGi = resGroups.map((g) => (g.length > 1 ? simBoxId(simSeq++) : altBoxId));

      allInsts.forEach((it) => {
        add({ id: it.nodeId, type: "resourceNode" }, routeM, it.c, {
          label: resources[it.id]?.resCd,
          kind: FLO_KIND.RESOURCE,
          raw: resources[it.id],
          dupKey: `res:${it.id}`,
          combGroup: boxIdOfGi[it.gi], // 자기 박스(동시 셋=실선 박스 / 단독=택1 박스)와 함께 이동
          sourcePos: resSrc,
        });
      });

      if (simCount === 0) {
        // 동시 셋 없음 → 자원 택1(OR): 후보 자원들을 점선 박스로 감싸고 박스 → 라우트(resIn).
        const resPts = allInsts.map((it) => toPos(routeM, it.c));
        groupNodes.push(makeGroupBox(altBoxId, resPts, { variant: "alt", label: "OR", sourcePos: resSrc, collapsible: true }));
        edges.push({ ...floEdgeBase, id: `e-altres-${pk}-${r.id}`, source: altBoxId, target: routeNode, targetHandle: ROUTE_RES_IN });
      } else if (onlyOneSim) {
        // 후보가 동시 셋 하나뿐 → 실선 결합 박스(AND) → 라우트. 택1 박스 없음.
        const gi = resGroups.findIndex((g) => g.length > 1);
        const boxPts = groupInsts[gi].map((it) => toPos(routeM, it.c));
        groupNodes.push(makeGroupBox(boxIdOfGi[gi], boxPts, { label: "AND", sourcePos: resSrc, collapsible: true }));
        edges.push({ ...floEdgeBase, id: `e-simres-${pk}-${r.id}`, source: boxIdOfGi[gi], target: routeNode, targetHandle: ROUTE_RES_IN });
      } else {
        // 혼합/복수 동시 셋: 바깥 점선 택1 박스로 감싸고, 각 동시 셋은 자기 실선 박스로 독립 이동한다.
        // (택1 박스는 draggable=false·정적. 셋 하나를 드래그하면 그 셋만 움직이고 다른 셋은 제자리.)
        // 연결선은 각 셋 박스에서 라우트로 직접 잇는다 → 셋을 옮겨도 박스와 함께 이동해 연결이 끊기지 않는다.
        const allPts = allInsts.map((it) => toPos(routeM, it.c));
        groupNodes.push(makeGroupBox(altBoxId, allPts, { variant: "alt", label: "OR", sourcePos: resSrc, collapsible: true }));
        resGroups.forEach((g, gi) => {
          if (g.length <= 1) return;
          const boxPts = groupInsts[gi].map((it) => toPos(routeM, it.c));
          // 각 동시 셋 실선 박스는 자기 멤버 자원과만 함께 이동(combGroup 없음 → 셋 독립 이동)
          groupNodes.push(makeGroupBox(boxIdOfGi[gi], boxPts, { label: "AND", sourcePos: resSrc }));
          edges.push({ ...floEdgeBase, id: `e-simres-${pk}-${r.id}-${gi}`, source: boxIdOfGi[gi], target: routeNode, targetHandle: ROUTE_RES_IN });
        });
        // 단독 자원(택1 후보)이 있으면 바깥 택1 박스에서 라우트로 연결.
        if (singleCount > 0) {
          edges.push({ ...floEdgeBase, id: `e-altres-${pk}-${r.id}`, source: altBoxId, target: routeNode, targetHandle: ROUTE_RES_IN });
        }
      }
    });
  });

  // ── 대체 자재 (완제품/루트) ────────────────────────────────────────────
  // 완제품 자체에 대체 공급이 걸린 경우("CHAIR 대신 CHAIR2 를 공급"). 완제품은 어디에도 투입되지
  // 않으므로 투입 공정이 없고(consmp_route_id NULL) 투입선도 없다 → 완제품 삼각형에 관계선만 잇는다.
  // 컬럼은 구성품과 달리 "같은 컬럼"이다: 루트 컬럼(stage 0)에는 루트 품목만 있고 ◇ 결합 박스도
  // 없으므로(박스는 자식들을 감싼다) 옆으로 밀 필요가 없고, 밀면 밴드 밖으로 나간다.
  // 스테이지 루프는 stageOf(생산 인스턴스의 자식들)만 돌아 루트를 방문하지 않으므로 여기서 따로 처리한다.
  rootKeys.forEach((rk) => addAltSupplyNodes(rk, instCol[rk], () => null));

  // ── 품목↔품목 직접 합류 선 화살표 제거 ────────────────────────────────
  // 구성품(또는 그 구성품을 묶은 결합 박스, 대체 자재)이 라우트(공정)를 거치지 않고 상위 품목으로
  // 곧장 이어지는 선은 품목과 품목 사이의 연결이므로 화살표(markerEnd)를 표시하지 않는다.
  edges.forEach((e) => {
    const fromItemSide =
      e.source.startsWith("item-") || e.source.startsWith("comb-") || e.source.startsWith("altsup-");
    if (fromItemSide && e.target.startsWith("item-")) e.markerEnd = undefined;
  });

  // ── 동일 원본(중복 인스턴스) 배지: dupKey별 개수/순번 부여 (dupTotal>1일 때 표시) ──
  const dupCount = {};
  nodes.forEach((n) => { if (n.data?.dupKey) dupCount[n.data.dupKey] = (dupCount[n.data.dupKey] ?? 0) + 1; });
  const dupSeen = {};
  nodes.forEach((n) => {
    const k = n.data?.dupKey;
    if (!k) return;
    dupSeen[k] = (dupSeen[k] ?? 0) + 1;
    n.data.dupTotal = dupCount[k];
    n.data.dupIndex = dupSeen[k];
  });

  // ── 단계(stage) 영역 밴드: stage마다 교차축 전체를 덮는 stripe(정확히 maxStage+1개) ──
  // 컬럼이 stage당 1칸으로 균일하므로 각 컬럼을 ±0.5로 감싼다. 색은 stage 짝/홀로 번갈아.
  const crossStart = cMin * GAP_C - PAD;
  const crossLen = (cMax - cMin) * GAP_C + NODE + 2 * PAD;

  const bandNodes = [];
  for (let stage = 0; stage <= maxStage; stage++) {
    const c = mOf(stage); // 컬럼 위치
    const mainStart = (c - 0.5) * GAP_M + OFF;
    const rect = isLR
      ? { x: mainStart, y: crossStart, width: GAP_M, height: crossLen }
      : { x: crossStart, y: mainStart, width: crossLen, height: GAP_M };
    bandNodes.push({
      id: `stage-${stage}`,
      type: "stageNode",
      position: { x: rect.x, y: rect.y },
      data: { width: rect.width, height: rect.height, parity: stage % 2, direction, stage },
      draggable: false,
      selectable: false,
      connectable: false,
      focusable: false,
      zIndex: -1,
      style: { zIndex: -1, pointerEvents: "none" },
    });
  }

  // 레이어 순서(뒤→앞): 단계 밴드 → 결합 박스 → 일반 노드/라벨
  return { nodes: [...bandNodes, ...groupNodes, ...nodes], edges, truncated, cycles: [...cycleSet] };
}
